import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { apiFetch } from '../../lib/api-client'
import { formatOere, parseKrToOere } from '../../lib/money'
import type { Lease } from '../contracts/wizard/types'
import {
  s, apiMsg, cardStyle, thStyle, tdStyle, fieldLabel, fieldControl, fmtDate,
  type Listing,
} from './listings-shared'
import { ErrorLine, ListingStatusChip, PageHeader } from './ListingParts'

function leaseLabel(l: Lease): string {
  return [l.property_address, l.unit_no].filter(Boolean).join(' – ')
}

export function ListingsPage() {
  const navigate = useNavigate()
  const [listings, setListings] = React.useState<Listing[]>([])
  const [leases, setLeases] = React.useState<Lease[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [showForm, setShowForm] = React.useState(false)

  const [leaseId, setLeaseId] = React.useState('')
  const [title, setTitle] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [rent, setRent] = React.useState('')
  const [formError, setFormError] = React.useState<string | null>(null)
  const [saving, setSaving] = React.useState(false)

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [ls, lease] = await Promise.all([
        apiFetch<Listing[]>('/api/v1/listings'),
        apiFetch<Lease[]>('/api/v1/leases'),
      ])
      setListings(ls)
      setLeases(lease)
    } catch {
      setError(s.loadError)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  const leaseById = React.useMemo(() => new Map(leases.map((l) => [l.id, l])), [leases])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!leaseId) return setFormError(s.errLease)
    if (!title.trim()) return setFormError(s.errTitle)
    const oere = parseKrToOere(rent)
    if (oere === null) return setFormError(s.errRent)
    setSaving(true)
    setFormError(null)
    try {
      const created = await apiFetch<Listing>('/api/v1/listings', {
        method: 'POST',
        body: JSON.stringify({
          lease_id: leaseId,
          title: title.trim(),
          description: description.trim() || null,
          husleje_oere: oere,
        }),
      })
      navigate(`/annoncering/${created.id}`)
    } catch (err) {
      setFormError(apiMsg(err, s.createError))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppLayout>
      <PageHeader
        title={s.title}
        subtitle={s.subtitle}
        action={
          !showForm ? (
            <Button onClick={() => setShowForm(true)}>
              <Plus size={14} />
              {s.create}
            </Button>
          ) : undefined
        }
      />

      {showForm && (
        <form onSubmit={(e) => void submit(e)} style={{ ...cardStyle, padding: 20, marginBottom: 20, maxWidth: 640 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 16 }}>{s.createTitle}</div>
          <div style={{ display: 'grid', gap: 14 }}>
            <div>
              <label htmlFor="listing-lease" style={fieldLabel}>{s.fieldLease}</label>
              <select
                id="listing-lease"
                value={leaseId}
                onChange={(e) => setLeaseId(e.target.value)}
                style={fieldControl}
              >
                <option value="">{s.fieldLeasePlaceholder}</option>
                {leases.map((l) => (
                  <option key={l.id} value={l.id}>{leaseLabel(l)}</option>
                ))}
              </select>
              {leases.length === 0 && (
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>{s.noLeases}</div>
              )}
            </div>
            <Input id="listing-title" label={s.fieldTitle} value={title} onChange={(e) => setTitle(e.target.value)} />
            <div>
              <label htmlFor="listing-description" style={fieldLabel}>{s.fieldDescription}</label>
              <textarea
                id="listing-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                style={{ ...fieldControl, height: 'auto', padding: '8px 10px', resize: 'vertical' }}
              />
            </div>
            <Input
              id="listing-rent"
              label={s.fieldRent}
              inputMode="decimal"
              placeholder="9.500,00"
              value={rent}
              onChange={(e) => setRent(e.target.value)}
            />
          </div>
          {formError && <ErrorLine>{formError}</ErrorLine>}
          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <Button type="submit" isLoading={saving}>{s.save}</Button>
            <Button type="button" variant="secondary" onClick={() => { setShowForm(false); setFormError(null) }}>
              {s.cancel}
            </Button>
          </div>
        </form>
      )}

      <div style={{ ...cardStyle, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, fontSize: 14, color: '#9ca3af' }}>{s.loading}</div>
        ) : error ? (
          <div style={{ padding: 32, fontSize: 14, color: '#dc2626' }}>{error}</div>
        ) : listings.length === 0 ? (
          <div style={{ padding: 32, fontSize: 14, color: '#9ca3af' }}>{s.empty}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e8eaed' }}>
                  <th style={thStyle}>{s.colTitle}</th>
                  <th style={thStyle}>{s.colLease}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{s.colRent}</th>
                  <th style={thStyle}>{s.colStatus}</th>
                  <th style={thStyle}>{s.colCreated}</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((l) => {
                  const lease = leaseById.get(l.lease_id)
                  return (
                    <tr
                      key={l.id}
                      onClick={() => navigate(`/annoncering/${l.id}`)}
                      style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    >
                      <td style={{ ...tdStyle, fontWeight: 600, color: '#111827', minWidth: 180 }}>
                        <a
                          href={`/annoncering/${l.id}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/annoncering/${l.id}`) }}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          {l.title}
                        </a>
                      </td>
                      <td style={{ ...tdStyle, minWidth: 180 }}>{lease ? leaseLabel(lease) : '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatOere(l.husleje_oere)}</td>
                      <td style={tdStyle}><ListingStatusChip status={l.status} /></td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(l.created_at)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
